import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  ALLERGEN_SAFETY_PILLAR_PATH,
  ALLERGEN_SAFETY_QA_GUIDE_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const canonicalUrl = `https://speakyourmenu.com${ALLERGEN_SAFETY_QA_GUIDE_PATH}`;
const pillarUrl = `https://speakyourmenu.com${ALLERGEN_SAFETY_PILLAR_PATH}`;
const pageTitle = "Allergen Q&A Guide for Servers | Speak Your Menu";
const pageDescription = "A practical allergen Q&A guide for restaurant servers who need clearer dietary answers, safer escalation language, and more consistent guest communication.";

const guideSections = [
  {
    title: "Answer with the shared standard",
    items: [
      "Teach staff to answer from the current ingredient and allergen standard, not from memory or an older version of the menu.",
      "Make sure every employee knows where the verified answer lives before service starts.",
      "Reinforce that speed matters, but accuracy matters more whenever a dietary question is at stake.",
    ],
  },
  {
    title: "Use clear table-side language",
    items: [
      "Give staff short phrases for what is in a dish, what can be modified, and when the kitchen must confirm the answer.",
      "Avoid vague language that sounds confident but leaves room for guest misunderstanding.",
      "Practice how to say no safely when a substitution cannot be confirmed or guaranteed.",
    ],
  },
  {
    title: "Drill the highest-risk questions",
    items: [
      "Focus on the dishes and allergy questions that come up most often during service.",
      "Repeat modification scenarios that staff routinely answer inconsistently.",
      "Use pre-shift drills to reinforce one ingredient or substitution theme at a time.",
    ],
  },
  {
    title: "Escalate without hesitation",
    items: [
      "Teach staff exactly when they must pause and verify with the kitchen or manager.",
      "Make escalation sound normal and trustworthy to the guest, not like a failure.",
      "Review any conflicting answers quickly so the same mistake does not carry into the next shift.",
    ],
  },
];

export default function AllergenSafetyQAGuidePage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Allergen Safety Guide", item: pillarUrl },
      { "@type": "ListItem", position: 3, name: "Allergen Q&A Guide for Servers", item: canonicalUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Q&amp;A Guide</span>
              <h1 className="homepage-hero-title resource-hero-title">An allergen Q&amp;A guide your servers can actually use during service.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                Use this guide to give your team clearer answer language, safer escalation habits, and more consistent responses when guests ask detailed dietary questions.
              </p>
              <div className="homepage-hero-actions resource-hero-actions">
                <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
                <Link to={ALLERGEN_SAFETY_PILLAR_PATH} className="resource-secondary-link">Read the full allergen-safety guide</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Use it weekly</span>
            <h2 className="homepage-section-title resource-section-title">Allergen Q&amp;A guide for servers</h2>
            <div className="resource-checklist-stack">
              {guideSections.map((section) => (
                <article key={section.title} className="homepage-surface-card resource-card">
                  <h3 className="homepage-card-title">{section.title}</h3>
                  <ul className="resource-bullet-list">
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">Why it matters</span>
              <h2 className="homepage-section-title resource-section-title">Most guest trust breaks when the answer sounds uncertain or changes from person to person.</h2>
              <p className="homepage-section-body resource-section-body">
                Guests do not expect perfection. They expect clear communication, safe verification, and consistent handling. This guide gives managers a shorter framework for coaching those moments without turning service into a lecture.
              </p>
            </article>
            <article className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Go deeper</span>
              <h2 className="homepage-card-title resource-aside-title">Need the full system behind the Q&amp;A guide?</h2>
              <p className="homepage-card-copy">The full allergen-safety guide explains how to structure ingredient accuracy, substitution rules, and escalation standards across the whole team.</p>
              <Link to={ALLERGEN_SAFETY_PILLAR_PATH} className="homepage-primary-cta resource-inline-cta">Read the guide</Link>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}